import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchIssues, fetchIssueDetails, downloadPaper } from '../utils/api';
import CitationModal from '../components/CitationModal';

const Issues = () => {
  const { id } = useParams();
  const [issues, setIssues] = useState([]);
  const [currentIssue, setCurrentIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [filteredPapers, setFilteredPapers] = useState([]);
  const [showCitationModal, setShowCitationModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [isDownloading, setIsDownloading] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const issuesData = await fetchIssues();
        setIssues(issuesData);

        // If an ID is provided, fetch that specific issue
        if (id) {
          const issueDetails = await fetchIssueDetails(id);
          setCurrentIssue(issueDetails);
        } else if (issuesData.length > 0) {
          // Otherwise, show the most recent issue
          const mostRecentIssue = issuesData.reduce((latest, issue) => {
            return new Date(issue.publicationDate) > new Date(latest.publicationDate) ? issue : latest;
          }, issuesData[0]);
          
          // Fetch details for the most recent issue
          const issueDetails = await fetchIssueDetails(mostRecentIssue.id);
          setCurrentIssue(issueDetails);
        }
      } catch (err) {
        console.error('Error loading issues:', err);
        setError('Failed to load journal issues. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    if (currentIssue && currentIssue.papers) {
      let results = [...currentIssue.papers];
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        results = results.filter(p => 
          p.title.toLowerCase().includes(query) || 
          p.abstract?.toLowerCase().includes(query) ||
          p.keywords?.toLowerCase().includes(query)
        );
      }
      
      if (categoryFilter) {
        results = results.filter(p => p.category === categoryFilter);
      }
      
      if (authorFilter) {
        const authorLower = authorFilter.toLowerCase();
        results = results.filter(p => 
          p.authors?.some(a => a.name.toLowerCase().includes(authorLower))
        );
      }
      
      setFilteredPapers(results);
    } else {
      setFilteredPapers([]);
    }
  }, [currentIssue, searchQuery, categoryFilter, authorFilter]);

  const handleDownload = async (paperId) => {
    try {
      setIsDownloading(paperId);
      await downloadPaper(paperId);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download paper. Please try again later.');
    } finally {
      setIsDownloading(null);
    }
  };

  const handleCite = (paper) => {
    setSelectedPaper(paper);
    setShowCitationModal(true);
  };

  const categories = currentIssue?.papers 
    ? [...new Set(currentIssue.papers.map(p => p.category))].filter(Boolean)
    : [];

  const handleIssueClick = async (issueId) => {
    try {
      setLoading(true);
      const issueDetails = await fetchIssueDetails(issueId);
      setCurrentIssue(issueDetails);
    } catch (err) {
      console.error('Error loading issue details:', err);
      setError('Failed to load issue details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !currentIssue) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading journal issues...</p>
      </div>
    );
  }

  if (error && !currentIssue) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-5">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <h1 className="mb-4 text-center">Journal Issues</h1>
          
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h4 className="mb-0">Browse Issues</h4>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-wrap">
                    {issues.length > 0 ? (
                      issues.map(issue => (
                        <button
                          key={issue.id}
                          className={`btn ${currentIssue && currentIssue.id === issue.id ? 'btn-primary' : 'btn-outline-primary'} m-1`}
                          onClick={() => handleIssueClick(issue.id)}
                        >
                          Vol. {issue.volume}, No. {issue.issue} ({new Date(issue.publicationDate).getFullYear()})
                        </button>
                      ))
                    ) : (
                      <p className="text-center w-100">No issues available at this time.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {currentIssue && (
            <div className="card mb-4">
              <div className="card-header bg-primary text-white">
                <h2 className="mb-0">{currentIssue.title}</h2>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <div className="d-flex flex-wrap justify-content-between mb-3">
                    <div>
                      <strong>Volume:</strong> {currentIssue.volume}
                    </div>
                    <div>
                      <strong>Issue:</strong> {currentIssue.issue}
                    </div>
                    <div>
                      <strong>Publication Date:</strong> {new Date(currentIssue.publicationDate).toLocaleDateString()}
                    </div>
                  </div>

                  {currentIssue.description && <p>{currentIssue.description}</p>}

                  {currentIssue.pdfUrl && (
                    <div className="mt-3">
                      <a
                        href={`/api/public/issues/${currentIssue.id}/download`}
                        className="btn btn-primary d-inline-flex align-items-center"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fas fa-file-pdf me-2 fa-lg"></i>
                        <strong>Download Complete Issue (PDF)</strong>
                      </a>
                    </div>
                  )}
                </div>

                <div className="bg-light p-3 rounded mb-4 border">
                  <h5 className="mb-3">Filter Articles</h5>
                  <div className="row g-3">
                    <div className="col-md-5">
                      <div className="input-group">
                        <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search title, keywords..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                      >
                        <option value="">All Categories</option>
                        {categories.map((cat, idx) => (
                          <option key={idx} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Filter by author..."
                        value={authorFilter}
                        onChange={(e) => setAuthorFilter(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <h4 className="mb-3">Articles in this Issue ({filteredPapers.length})</h4>
                {filteredPapers.length > 0 ? (
                  <div className="list-group">
                    {filteredPapers.map(paper => (
                      <div key={paper.id} className="list-group-item list-group-item-action p-4">
                        <div className="d-flex w-100 justify-content-between align-items-start mb-2">
                          <Link 
                            to={`/paper/${paper.id}`}
                            state={{ from: '/issues', fromLabel: 'Issues' }}
                            className="text-decoration-none"
                          >
                            <h5 className="mb-1 text-primary">{paper.title}</h5>
                          </Link>
                          <small className="text-muted fw-bold">Pages: {paper.pages || 'N/A'}</small>
                        </div>
                        <p className="mb-2 text-secondary">
                          <i className="fas fa-user-edit me-2 small"></i>
                          {paper.authors?.map(author => author.name).join(', ')}
                        </p>
                        <div className="mb-3">
                          {paper.keywords?.split(',').map((keyword, index) => (
                            <span key={index} className="badge bg-light text-dark border me-1 mb-1">
                              {keyword.trim()}
                            </span>
                          ))}
                        </div>
                        <div className="d-flex gap-2">
                          <Link
                            to={`/paper/${paper.id}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="fas fa-info-circle me-1"></i> Details
                          </Link>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleDownload(paper.id)}
                            disabled={isDownloading === paper.id}
                          >
                            {isDownloading === paper.id ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <><i className="fas fa-download me-1"></i> Download</>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleCite(paper)}
                          >
                            <i className="fas fa-quote-right me-1"></i> Cite
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-info">
                    {searchQuery || categoryFilter || authorFilter 
                      ? 'No articles match your filters.' 
                      : "This issue doesn't contain any papers yet."}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {!currentIssue && !loading && (
            <div className="alert alert-warning">
              No issues are currently available.
            </div>
          )}
          
          <div className="text-center mt-4">
            <Link to="/" className="btn btn-outline-secondary">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
      {showCitationModal && selectedPaper && (
        <CitationModal
          paper={selectedPaper}
          onClose={() => setShowCitationModal(false)}
        />
      )}
    </>
  );
};

export default Issues;
